/**
 * Returns the CDN URL for the plugin bundle.
 *
 * The bundle is deployed per-environment by the backend, so the CDN must match
 * the backend the app is *connected to* (`VITE_BASE_URL`), not the frontend's
 * own build stage (`VITE_STAGE`). These agree in every deployed env; they
 * diverge only in local dev, where the frontend runs at `VITE_STAGE=local` but
 * is pointed at a real backend (e.g. prod) — there the bundle lives on that
 * backend's CDN, not dev-cdn.
 *
 * Mirrors the backend logic in config-manager.ts (cdnPaths + cdnPath getter).
 * TODO: Consider moving to a shared utility in ig-utils.
 */
const PROD_CDN = "https://cdn.intelligems.io";

export const getPluginCdnUrl = (): string => {
  const stage = resolveConnectedStage();
  if (!stage || stage === "prod" || stage === "beta") {
    return PROD_CDN;
  }
  if (stage === "local" || stage === "test") {
    return "https://dev-cdn.intelligems.io";
  }
  return `https://${stage}-cdn.intelligems.io`;
};

/**
 * Derive the connected backend's stage from `VITE_BASE_URL`'s host — e.g.
 * `api.intelligems.io` → `prod`, `dev-3.intelligems.io` → `dev-3`,
 * `guido-local.intelligems.io` → `local`. Falls back to `VITE_STAGE` when the
 * base URL is missing/unparseable (e.g. tests).
 */
function resolveConnectedStage(): string | undefined {
  const baseUrl = import.meta.env.VITE_BASE_URL;
  if (!baseUrl) {
    return import.meta.env.VITE_STAGE;
  }

  try {
    const label = new URL(baseUrl).hostname.split(".")[0];
    if (label === "api") {
      return "prod";
    }
    if (label === "beta-api") {
      return "beta";
    }
    if (label.endsWith("local")) {
      return "local";
    }
    return label;
  } catch {
    return import.meta.env.VITE_STAGE;
  }
}
